import React from 'react';

type MetricsWorkspaceTab = 'overview' | 'service' | 'reliability' | 'customers' | 'sla' | 'inventory' | 'team';
type MetricsWorkspaceIncludeBlock = { id: string; label: string; description: string };
type PortalInvitePreview = {
  contactName: string;
  organizationSlug: string;
  username: string;
  temporaryPassword: string;
  loginUrl: string;
  emailTo?: string;
};

type Props = {
  styles: any;
  showMetricsConfig: boolean;
  metricsWorkspaceTab: MetricsWorkspaceTab;
  metricsWorkspaceLabel: string;
  metricsWorkspaceIncludeBlocks: MetricsWorkspaceIncludeBlock[];
  metricsWorkspaceDraftIncludes: string[];
  setMetricsWorkspaceIncludesInDraft: (workspace: MetricsWorkspaceTab, includes: string[]) => void;
  cancelMetricsDashboardEdit: () => void;
  saveMetricsDashboard: () => void;
  showEditModal: boolean;
  setShowEditModal: React.Dispatch<React.SetStateAction<boolean>>;
  editingModel: {
    modelNumber: string;
    manufacturer: string;
    serialNumber: string;
    assetNumber: string;
    deviceId: string;
  };
  setEditingModel: React.Dispatch<React.SetStateAction<{
    modelNumber: string;
    manufacturer: string;
    serialNumber: string;
    assetNumber: string;
    deviceId: string;
  }>>;
  devices: any[];
  verifyModel: (action: 'approve' | 'edit' | 'reject') => void | Promise<void>;
  portalInvitePreview: PortalInvitePreview | null;
  setPortalInvitePreview: React.Dispatch<React.SetStateAction<PortalInvitePreview | null>>;
  portalInviteCopied: 'username' | 'password' | 'url' | null;
  copyPortalInviteValue: (value: string, field: 'username' | 'password' | 'url') => Promise<void>;
};

export const TechDashboardUtilityModals = React.memo(function TechDashboardUtilityModals({
  styles,
  showMetricsConfig,
  metricsWorkspaceTab,
  metricsWorkspaceLabel,
  metricsWorkspaceIncludeBlocks,
  metricsWorkspaceDraftIncludes,
  setMetricsWorkspaceIncludesInDraft,
  cancelMetricsDashboardEdit,
  saveMetricsDashboard,
  showEditModal,
  setShowEditModal,
  editingModel,
  setEditingModel,
  devices,
  verifyModel,
  portalInvitePreview,
  setPortalInvitePreview,
  portalInviteCopied,
  copyPortalInviteValue
}: Props) {
  return (
    <>
      {showMetricsConfig && (
        <div style={styles.modal}>
          <div style={{ ...styles.modalContent, ...styles.metricsIncludeModalContent }} onClick={(e) => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <div style={styles.metricsIncludeModalHeader}>
                <h2 style={styles.modalTitle}>Include {metricsWorkspaceLabel} Blocks</h2>
                <div style={styles.metricsIncludeModalIntro}>
                  Excluded blocks stay off for this page, so the Insights payload can skip work that is not needed.
                </div>
              </div>
              <button style={styles.closeButton} onClick={cancelMetricsDashboardEdit}>X</button>
            </div>
            <div style={styles.metricsIncludeList}>
              {metricsWorkspaceIncludeBlocks.length === 0 ? (
                <div style={styles.insightsEmptyState}>
                  Include controls are not wired for this Insights page yet.
                </div>
              ) : (
                metricsWorkspaceIncludeBlocks.map(block => {
                  const isIncluded = metricsWorkspaceDraftIncludes.includes(block.id);
                  const nextIncludes = isIncluded
                    ? metricsWorkspaceDraftIncludes.filter(item => item !== block.id)
                    : [...metricsWorkspaceDraftIncludes, block.id];
                  return (
                    <div key={block.id} style={styles.metricsIncludeRow}>
                      <div style={styles.metricsIncludeMain}>
                        <div style={styles.metricsIncludeLabel}>{block.label}</div>
                        <div style={styles.metricsIncludeDescription}>{block.description}</div>
                      </div>
                      <button
                        type="button"
                        style={{
                          ...styles.metricsIncludeToggle,
                          ...(isIncluded
                            ? styles.metricsIncludeToggleActive
                            : styles.metricsIncludeToggleInactive)
                        }}
                        onClick={() => setMetricsWorkspaceIncludesInDraft(metricsWorkspaceTab, nextIncludes)}
                      >
                        {isIncluded ? 'Included' : 'Excluded'}
                      </button>
                    </div>
                  );
                })
              )}
            </div>
            <div style={{ marginTop: '1rem', display: 'flex', justifyContent: 'flex-end', gap: '0.75rem' }}>
              <button
                type="button"
                style={{ ...styles.button, ...styles.secondaryButton }}
                onClick={cancelMetricsDashboardEdit}
              >
                Cancel
              </button>
              <button
                type="button"
                style={{ ...styles.button, ...styles.primaryButton }}
                onClick={saveMetricsDashboard}
                disabled={metricsWorkspaceIncludeBlocks.length === 0}
              >
                Save Includes
              </button>
            </div>
          </div>
        </div>
      )}

      {showEditModal && (
        <div style={styles.modal} onClick={() => setShowEditModal(false)}>
          <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <h2 style={styles.modalTitle}>Edit Device Information</h2>
              <button style={styles.closeButton} onClick={() => setShowEditModal(false)}>X</button>
            </div>

            <div style={styles.infoSection}>
              <label style={styles.label}>Model Number</label>
              <input
                type="text"
                style={styles.inputField}
                value={editingModel.modelNumber}
                onChange={(e) => setEditingModel({ ...editingModel, modelNumber: e.target.value })}
              />
            </div>

            <div style={styles.infoSection}>
              <label style={styles.label}>Manufacturer</label>
              <input
                type="text"
                style={styles.inputField}
                value={editingModel.manufacturer}
                onChange={(e) => setEditingModel({ ...editingModel, manufacturer: e.target.value })}
              />
            </div>

            {(editingModel.serialNumber || editingModel.assetNumber) && (
              <>
                <div style={styles.infoSection}>
                  <label style={styles.label}>Link Existing Registered Device</label>
                  <select
                    style={styles.inputField}
                    value={editingModel.deviceId}
                    onChange={(e) => setEditingModel({ ...editingModel, deviceId: e.target.value })}
                  >
                    <option value="">Use the temporary/new device record</option>
                    {(Array.isArray(devices) ? devices : []).map(device => {
                      const serial = String(device?.serialNumber || '').trim();
                      const asset = String(device?.assetNumber || '').trim();
                      const customer =
                        String(device?.customer?.companyName || device?.customer?.company || '').trim();
                      const identity = asset
                        ? `Asset ${asset}${serial ? ` | Serial ${serial}` : ''}`
                        : serial
                          ? `Serial ${serial}`
                          : 'No serial recorded';
                      const model = String(device?.modelNumber || 'Device').trim();
                      const label = [model, identity, customer].filter(Boolean).join(' - ');
                      return (
                        <option key={device.id} value={device.id}>
                          {label}
                        </option>
                      );
                    })}
                  </select>
                </div>

                <div style={styles.infoSection}>
                  <label style={styles.label}>Serial Number</label>
                  <input
                    type="text"
                    style={styles.inputField}
                    value={editingModel.serialNumber}
                    onChange={(e) => setEditingModel({ ...editingModel, serialNumber: e.target.value })}
                  />
                </div>

                <div style={styles.infoSection}>
                  <label style={styles.label}>Asset Number</label>
                  <input
                    type="text"
                    style={styles.inputField}
                    value={editingModel.assetNumber}
                    onChange={(e) => setEditingModel({ ...editingModel, assetNumber: e.target.value })}
                  />
                </div>
              </>
            )}

            <button
              style={{ ...styles.button, ...styles.primaryButton, width: '100%' }}
              onClick={() => verifyModel('edit')}
            >
              Save & Approve
            </button>
          </div>
        </div>
      )}

      {portalInvitePreview && (
        <div style={styles.modal} onClick={() => setPortalInvitePreview(null)}>
          <div style={styles.portalInviteModal} onClick={(e) => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <h2 style={styles.modalTitle}>Customer Portal Access Ready</h2>
              <button style={styles.closeButton} onClick={() => setPortalInvitePreview(null)}>X</button>
            </div>
            <div style={styles.portalInviteBody}>
              <div style={styles.portalInviteRow}>
                <span style={styles.portalInviteLabel}>Contact</span>
                <span>{portalInvitePreview.contactName}</span>
              </div>
              <div style={styles.portalInviteRow}>
                <span style={styles.portalInviteLabel}>Organization</span>
                <span>{portalInvitePreview.organizationSlug}</span>
              </div>
              <div style={styles.portalInviteRow}>
                <span style={styles.portalInviteLabel}>Username</span>
                <div style={styles.portalInviteValueWrap}>
                  <code style={styles.portalInviteCode}>{portalInvitePreview.username}</code>
                  <button
                    type="button"
                    style={{ ...styles.actionPill, ...styles.actionPillNeutral }}
                    onClick={() => copyPortalInviteValue(portalInvitePreview.username, 'username')}
                  >
                    {portalInviteCopied === 'username' ? 'Copied' : 'Copy'}
                  </button>
                </div>
              </div>
              <div style={styles.portalInviteRow}>
                <span style={styles.portalInviteLabel}>Temporary password</span>
                <div style={styles.portalInviteValueWrap}>
                  <code style={styles.portalInviteCode}>{portalInvitePreview.temporaryPassword || 'Generated'}</code>
                  <button
                    type="button"
                    style={{ ...styles.actionPill, ...styles.actionPillNeutral }}
                    onClick={() => copyPortalInviteValue(portalInvitePreview.temporaryPassword || '', 'password')}
                  >
                    {portalInviteCopied === 'password' ? 'Copied' : 'Copy'}
                  </button>
                </div>
              </div>
              <div style={styles.portalInviteRow}>
                <span style={styles.portalInviteLabel}>Login URL</span>
                <div style={styles.portalInviteValueWrap}>
                  <code style={styles.portalInviteCodeLong}>{portalInvitePreview.loginUrl}</code>
                  <button
                    type="button"
                    style={{ ...styles.actionPill, ...styles.actionPillNeutral }}
                    onClick={() => copyPortalInviteValue(portalInvitePreview.loginUrl, 'url')}
                  >
                    {portalInviteCopied === 'url' ? 'Copied' : 'Copy'}
                  </button>
                </div>
              </div>
              <div style={styles.portalInviteHint}>
                Email sending is not enabled yet. Share these details manually for now.
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
});

